import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Trash } from '@styled-icons/bootstrap/Trash';
import usePageTitle from 'hooks/usePageTitle';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { Tabs } from 'DesignSystem';
import { ITab } from 'DesignSystem/Primitives/Tabs/Tabs';
import React, { FC, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import { EDocumentLibraryTabs } from '../types/document.types';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

/**
 * Tabs which are shown in the document library
 * @param onlyForBuyer - if true, the tab is only shown for partners
 */
const DOCUMENT_TABS: (ITab<EDocumentLibraryTabs> & {
  onlyForBuyer?: boolean;
})[] = [
  {
    key: EDocumentLibraryTabs.INTERNAL,
    label: 'Internal',
  },
  {
    key: EDocumentLibraryTabs.SHARED,
    label: 'Shared with me',
    onlyForBuyer: true,
  },
];

export const DeleteIcon = styled(Trash)(({ theme }) => ({
  color: theme.palette.secondary.main,
}));

const DocumentsLayout: FC = () => {
  usePageTitle('Documents');
  const location = useLocation();
  const navigate = useNavigate();
  const { plan } = useCompanyPlanContext();
  const isPartner = plan === ECustomerPlan.FREEMIUM;

  const selectedTab: EDocumentLibraryTabs = location.pathname.split('/')[2] as EDocumentLibraryTabs;

  const handleChangeTab = (tab: EDocumentLibraryTabs) => {
    if (tab === EDocumentLibraryTabs.INTERNAL) {
      navigate(`/documents/internal${location.search}`);
    } else {
      navigate(`/documents/${tab}${location.search}`);
    }
  };

  const tabs = useMemo(
    () => DOCUMENT_TABS.filter(({ onlyForBuyer }) => !onlyForBuyer || (onlyForBuyer && !isPartner)),
    [isPartner]
  );

  return (
    <PageContainer>
      <PageTitle title="Documents" />

      <Box mt={3} mb={1}>
        <Tabs tabs={tabs} selectedTab={selectedTab} onChange={handleChangeTab} />
      </Box>

      <Outlet />
    </PageContainer>
  );
};

export default DocumentsLayout;
